import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { DayNavigator } from "@/components/admin/DayNavigator";
import { todayIso } from "@/lib/dates";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { EmptyState } from "@/components/feedback/EmptyState";
import { toast } from "@/components/feedback/ToastHost";
import { SnapshotTable } from "@/components/data/SnapshotTable";
import { errorMessage } from "@/lib/errors";
import { listDaySnapshots, updateSnapshot } from "@/services/api/snapshots";
import type { LoadSnapshot } from "@/services/api/types";

/** Editable depot reference cell: saves on blur and mirrors to the live load. */
function DepotReferenceCell({
  row,
  onSaved,
}: {
  row: LoadSnapshot;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState(row.depot_reference_number ?? "");
  const [saving, setSaving] = useState(false);
  const update = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Parameters<typeof updateSnapshot>[1] }) =>
      updateSnapshot(id, data),
    onSuccess: () => {
      toast.success(t("depot.referenceSaved"));
      onSaved();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const save = (next: string) => {
    if (next === (row.depot_reference_number ?? "")) return;
    setSaving(true);
    update.mutate(
      { id: row.id, data: { depot_reference_number: next } },
      {
        onSettled: () => setSaving(false),
        onError: () => setValue(row.depot_reference_number ?? ""),
      },
    );
  };

  return (
    <Input
      value={value}
      disabled={saving}
      onChange={(event) => setValue(event.target.value)}
      onBlur={() => save(value)}
      aria-label={t("loadboard.depotReference")}
    />
  );
}

/** Storage depot dashboard: a day's published loads with an editable reference. */
export function DepotDashboardPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [day, setDay] = useState(todayIso());
  const { data, isLoading } = useQuery({
    queryKey: ["snapshots", day],
    queryFn: () => listDaySnapshots(day),
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ["snapshots", day] });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-semibold">{t("depot.title")}</h1>
      </div>
      <DayNavigator day={day} onChange={setDay} />
      {isLoading ? (
        <Card>
          <CardContent className="py-8 text-sm text-muted-foreground">{t("common.loading")}</CardContent>
        </Card>
      ) : (data?.length ?? 0) === 0 ? (
        <EmptyState title={t("loadboard.noPublished")} />
      ) : (
        <SnapshotTable
          rows={data ?? []}
          trailingHeader={t("loadboard.depotReference")}
          renderTrailing={(row) => <DepotReferenceCell row={row} onSaved={invalidate} />}
        />
      )}
    </div>
  );
}
